import { type PropsWithChildren } from 'react';
import { debounceTime, filter, fromEvent, map, tap } from 'rxjs';

import { useSubscriptions } from '@/hook';
import { filterKey, isButtonElement, isInputElement } from '@/util';

import { useFileBrowserContext } from './context';

const VFileBrowserEventListener = ({ children }: PropsWithChildren) => {
  const {
    contentItemNodeList,
    currentDir,
    itemByUuid,
    selectedItemNodeList,
    selectedItemUuidList,

    selectItemList,
  } = useFileBrowserContext();

  useSubscriptions(
    () => [
      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter(e => !isInputElement(e.target)),
          filterKey(['a'], {
            conditionSpecialKey: ({ alt, ctrl, meta, shift }) =>
              ((ctrl && !meta) || (!ctrl && meta)) && !alt && !shift,
            exactCase: true,
          }),
          tap(e => {
            e.preventDefault();
            selectItemList(contentItemNodeList.orgList);
          }),
        )
        .subscribe(),

      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter(e => !isInputElement(e.target)),
          filter(e => !isButtonElement(e.target)),
          filterKey(['Escape'], {
            conditionSpecialKey: ({ alt, ctrl, meta, shift }) =>
              !alt && !ctrl && !meta && !shift,
          }),
          tap(e => {
            e.preventDefault();
            selectItemList([]);
          }),
        )
        .subscribe(),

      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter(e => !isButtonElement(e.target)),
          filter(e => !isInputElement(e.target)),
          filterKey(['ArrowDown', 'ArrowUp'], {
            conditionSpecialKey: ({ alt, ctrl, meta, shift }) =>
              !alt && !ctrl && !meta && shift,
          }),
          tap(e => e.preventDefault()),
          debounceTime(10),
          map(e => (e.key === 'ArrowDown' ? 1 : -1)),
          map(addedIndex => {
            const lastSelectedItem = selectedItemNodeList.last()?.value;
            const startIndex =
              lastSelectedItem == null
                ? -1
                : contentItemNodeList.findIndexByUuid(lastSelectedItem.uuid);

            return startIndex + addedIndex;
          }),
          filter(nextSelectedIndex => nextSelectedIndex >= 0),
          map(
            nextSelectedIndex =>
              contentItemNodeList.getAt(nextSelectedIndex)?.value,
          ),
          filter(nextSelectedItem => nextSelectedItem != null),
          tap(nextSelectedItem => {
            selectItemList(
              selectedItemNodeList.orgList.concat(nextSelectedItem),
            );
          }),
        )
        .subscribe(),

      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter(e => !isButtonElement(e.target)),
          filter(e => !isInputElement(e.target)),
          filterKey(['ArrowDown', 'ArrowUp'], {
            conditionSpecialKey: ({ alt, ctrl, meta, shift }) =>
              !alt && !ctrl && !meta && !shift,
          }),
          tap(e => e.preventDefault()),
          debounceTime(10),
          map(e => (e.key === 'ArrowDown' ? 1 : -1)),
          map(addedIndex => {
            const lastSelectedItem = selectedItemNodeList.last()?.value;
            const startIndex =
              lastSelectedItem == null
                ? -1
                : contentItemNodeList.findIndexByUuid(lastSelectedItem.uuid);

            return startIndex + addedIndex;
          }),
          filter(nextSelectedIndex => nextSelectedIndex >= 0),
          map(nextSelectedIndex =>
            contentItemNodeList.getAt(nextSelectedIndex),
          ),
          filter(nextSelectedItem => nextSelectedItem != null),
          tap(nextSelectedItem => selectItemList([nextSelectedItem.value])),
        )
        .subscribe(),
    ],
    [currentDir, itemByUuid, selectedItemNodeList],
  );

  useSubscriptions(
    () => [
      fromEvent<PointerEvent & { target: HTMLElement }>(window, 'click')
        .pipe(
          map(e => ({
            isMulti: (e.ctrlKey && !e.metaKey) || (!e.ctrlKey && e.metaKey),
            $target: e.target.dataset.uuid
              ? e.target
              : e.target.closest<HTMLElement>(`[data-id]`),
          })),
          filter(({ $target }) => {
            return (
              !!$target &&
              !!$target.dataset.id &&
              // TODO: $target.querySelector('[aria-label="name"]') 임시 처방
              $target.querySelector('[aria-label="name"]')?.textContent !== '..'
            );
          }),
          map(({ isMulti, $target }) => ({
            isMulti,
            id: $target!.dataset.id!,
          })),
          tap(({ isMulti, id }) => {
            if (!isMulti) return selectItemList([id]);

            const findedIndex = selectedItemUuidList.findIndex(v => id === v);
            const newUuidList = [...selectedItemUuidList];

            findedIndex >= 0
              ? newUuidList.splice(findedIndex, 1)
              : newUuidList.push(id);

            selectItemList(newUuidList);
          }),
        )
        .subscribe(),
    ],
    [currentDir, itemByUuid, selectedItemUuidList],
  );

  return <>{children}</>;
};

export default VFileBrowserEventListener;
